import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { useGroupContractProcess } from '../contexts/GroupContractProcessContext';
import { DocumentScopeEnum, GenericDocumentDto } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';

/**
 * Liste les documents générés pour le contrat collectif (DocumentContrat liés
 * au contrat de groupe). Mêmes cards que la section documents du parcours
 * individuel (`DocumentCardItem`) : badge PDF + titre + bouton œil.
 * Les mises à jour temps réel (envoi en signature, signature signée…) arrivent
 * via dataVersion : le provider écoute le hub SignalR collectif et rafraîchit.
 * Seuls les documents du contrat (scope Contrat) sont listés ici — les docs
 * produit sont visibles dans l'onglet Documents du détail.
 */
export const GroupContractDocumentsSection = () => {
  const { groupContractId, dataVersion } = useGroupContractProcess();
  const { openDocumentViewer } = useDocumentViewerDialog();

  const [docs, setDocs] = useState<GenericDocumentDto[]>([]);
  // Loader visible uniquement au premier chargement : les rechargements suivants
  // mettent la liste à jour en place, sans flash.
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const response = await contractsAPI.getGroupContractDocuments({ groupContractId });
      setDocs(response.documents.filter((doc) => doc.scope === DocumentScopeEnum.Contract));
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [groupContractId]);

  useEffect(() => {
    load();
  }, [load, dataVersion]);

  const handleView = (doc: GenericDocumentDto) => {
    if (!doc.storedFileId) return;
    openDocumentViewer({ title: doc.label, storedFileId: doc.storedFileId });
  };

  return (
    <CardsSection
      title="Documents"
      titleAside={(
        <button
          type="button"
          onClick={load}
          title="Actualiser"
          className="text-gray-400 hover:text-gray-700"
        >
          <RefreshCw size={16} />
        </button>
      )}
    >
      {docs.length === 0 ? (
        <div className="text-sm text-gray-500 italic">
          {loading ? 'Chargement…' : "Aucun document généré pour l'instant."}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center p-4 border rounded-xl bg-white"
            >
              <div className="grow flex gap-4">
                <GenericBadge
                  color={GenericColors.Primary}
                  className="font-bold"
                  label="PDF"
                  badgeType="sm-bold"
                />
                <div>
                  <p className="font text-primary">{doc.label}</p>
                </div>
              </div>
              {doc.storedFileId ? (
                <div className="relative flex items-center gap-2">
                  <Button
                    color="slate"
                    round
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      handleView(doc);
                    }}
                  >
                    <EyeIcon />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <GenericLoading size={Sizes.sm} loading />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </CardsSection>
  );
};
